/**
 * Deliverable 1 — Physics Config Schema (THE feel spine).
 *
 * One typed object that fully parameterizes platformer feel. Every "form"
 * (Small/Big/Fire/...) and every future user-tunable control preset is just a
 * partial override merged over a base config — see forms.ts and mergeConfig().
 *
 * UNITS (world space): positions/sizes in PIXELS, velocities in px/sec,
 * accelerations in px/sec². The engine runs a fixed 60 Hz step (GameLoop.ts);
 * these are real per-second values, so they read the same regardless of frame
 * rate. World scale is 1px = 1px; tileSize is 64 (LevelFormat.ts).
 *
 * Y axis points DOWN (Pixi screen convention): negative vy = moving up.
 * Jump impulses are therefore stored as positive magnitudes and applied upward.
 */
export interface PhysicsConfig {
  // ─── Horizontal ────────────────────────────────────────────────────────────
  /** Top speed when walking (run button NOT held). Lower = floatier, more deliberate. */
  walkMaxSpeed: number;
  /** Top speed when the run button is held. Gap over walk = how much "gear 2" matters. */
  runMaxSpeed: number;
  /** How fast you reach top speed on the ground. High = snappy/twitchy, low = slidey ramp-up. */
  groundAccel: number;
  /** How fast you stop when no input on the ground. Usually > accel so stops feel crisp. */
  groundDecel: number;
  /** Horizontal accel while airborne. Weaker than ground = committed jumps, less mid-air steering. */
  airAccel: number;
  /** Passive horizontal decel while airborne with no input. Low = preserves jump momentum. */
  airDecel: number;
  /** Extra decel applied during the turn-around skid (input opposes velocity). High = sharp pivots. */
  skidDecel: number;
  /** Min opposing speed (px/s) before a direction reversal counts as a skid vs normal decel. */
  skidThreshold: number;
  /**
   * Air-brake decel (the decel-symmetry split — handoff §1). The rate applied when AIRBORNE
   * AND input opposes velocity over `skidThreshold` — the air mirror of the grounded skid
   * branch. The collapse this fixes: today an airborne reversal falls into the *accel*
   * branch (airAccelEff), so there is no distinct air-brake knob, which makes "sticky
   * momentum you can still turn" (MOMENTUM-NINJA) impossible. 0 = DISABLED → the air-reversal
   * stays on today's accel path → CLASSIC/PRECISION/COMBAT bit-identical (the inert default).
   * MOMENTUM-NINJA sets it > 0 for a moderate, distinct air-brake (low air-coast + turnable).
   */
  airBrakeDecel: number;
  /**
   * MOMENTUM (feel-research 2026-06-30): per-step MULTIPLICATIVE air-drag for the COAST
   * (airborne with NO horizontal input). N++'s air friction is 100% multiplicative
   * (`vx *= k` each frame, k≈0.99) — a speed-PROPORTIONAL bleed that a flat px/s² decel
   * cannot reproduce at all speeds (the N++ "glide, momentum persists" signature). When
   * > 0 it REPLACES the flat air-coast decel for the no-input airborne case only (a held
   * direction still uses the accel/brake model, so it never fights the additive wall-jump
   * build). Pure multiply → IEEE-754 bit-stable / determinism-lint-clean. 0 (the default)
   * ⇒ the flat `airDecel` coast as before → every other archetype bit-identical. Typical
   * N++ value ≈ 0.985–0.99 (lower = stickier bleed); 1.0 would be a no-op.
   */
  airDragMultiplier: number;

  // ─── Vertical / jump ───────────────────────────────────────────────────────
  /** Upward launch speed at the instant of jump (magnitude). Bigger = higher ceiling. */
  jumpVelocity: number;
  /** Downward accel while falling (or rising with jump held). The "weight" of the character. */
  gravity: number;
  /** Heavier gravity applied while RISING after jump is released early → variable jump height. */
  jumpReleaseGravity: number;
  /** Max downward speed. Caps fall speed so long drops stay controllable. */
  terminalVelocity: number;
  /** Grace window (ms) after walking off a ledge during which a jump still fires. Forgiveness. */
  coyoteTimeMs: number;
  /** Window (ms): a jump pressed just BEFORE landing is remembered and fires on touchdown. */
  jumpBufferMs: number;
  /**
   * APEX-HANG (DASH-PRECISION / Celeste, feel-research 2026-06-30). Celeste applies HALF
   * gravity near the top of a jump ("the top of your jump has half gravity applied" —
   * Maddy Thorson) — a brief float that gives time to read the landing and reads as
   * pleasant hang. When `apexHangThreshold > 0`: while RISING-or-near-apex with
   * |vy| < apexHangThreshold AND the jump is still held (not an early-release cut),
   * gravity is `apexHangGravity` instead of the normal `gravity`. 0 (the default) =
   * DISABLED → the branch never runs → CLASSIC / every other archetype bit-identical.
   * `apexHangThreshold` is the |vy| (px/s) window the hang applies within.
   */
  apexHangThreshold: number;
  /** Gravity (px/s²) used inside the apex-hang window (typically ≈ half `gravity`). Only
   *  read when `apexHangThreshold > 0`; inert (0) otherwise. */
  apexHangGravity: number;

  // ─── Speed-dependent (optional hooks) ───────────────────────────────────────
  /**
   * SMW-style: fraction of horizontal speed (relative to runMaxSpeed) that boosts
   * jump launch velocity. 0 = off (jump height independent of run speed).
   * e.g. 0.15 → at full run, jumpVelocity is boosted by 15%.
   */
  speedToJumpScale: number;

  // ─── Air authority (archetype signature — PRECISION) ─────────────────────────
  /**
   * How much GROUND horizontal authority applies in the air, 0..1. The airborne
   * accel/decel rates are blended from the air-specific values toward the ground
   * values by this fraction:
   *   effectiveAirAccel = lerp(airAccel, groundAccel, airControl)
   *   effectiveAirDecel = lerp(airDecel, groundDecel, airControl)
   * 0 = the legacy independent air model (airAccel/airDecel verbatim — the CLASSIC
   * default, so existing feel is bit-identical); 1 = full ground authority in the air
   * = near-total mid-flight steering (the Super-Meat-Boy "no air-momentum" feel —
   * the PRECISION archetype's SIGNATURE value). A single dial Isaac tunes live, and
   * it composes with the existing airAccel/airDecel rather than replacing them.
   */
  airControl: number;

  // ─── Wall verbs (archetype — PRECISION) ──────────────────────────────────────
  // These are the data behind the wallSlide / wallJump verbs. They are INERT at the
  // base defaults (0); a verb only runs when its archetype enables it (see Archetype.ts
  // / Player). So a CLASSIC form never touches wall code and stays byte-identical.
  /**
   * While in the passive wall-slide state, downward velocity is clamped to this px/s
   * (a slow controlled cling-slide). 0 with the verb disabled = no clamp. Lower = a
   * stickier wall.
   */
  wallSlideMaxFallSpeed: number;
  /**
   * Horizontal launch speed (px/s, magnitude) of a wall-jump — applied AWAY from the
   * wall. The push direction is `-wallSide` (wall on the right → launch left).
   */
  wallJumpPushX: number;
  /** Upward launch speed (px/s, magnitude) of a wall-jump (applied as negative vy). */
  wallJumpVelocityY: number;
  /**
   * Fixed steps after a wall-jump during which horizontal input authority is suppressed
   * so the kick actually carries the player off the wall before full (near-total, under
   * PRECISION) air control snaps back and cancels it. Keep small (≈6–9 = 0.1–0.15 s).
   * Integer step counter → deterministic across a replay.
   */
  wallJumpControlLockSteps: number;
  /**
   * MOMENTUM-NINJA (handoff §3): when true, a wall-jump is ADDITIVE — it REFLECTS the
   * player's incoming speed off the wall and ADDS the kick, so alternating-wall kicks build
   * speed ("wall jump up alternating sides and build up a ton of speed", N++). false (the
   * default — PRECISION/every other archetype) = the kick SETS vx (`vx = -wallSide*pushX`),
   * discarding prior speed, so feel is unchanged. NOTE the handoff's literal `vx += -wallSide
   * *pushX` CANCELS on alternation (each kick flips sign vs the incoming direction); the
   * correct generalization that actually builds speed is reflect-and-add (preserve magnitude,
   * redirect to the launch side, add the kick) — that is what "momentum survives the
   * wall-jump" means. See Player.update's wall-jump branch.
   */
  wallJumpAdditive: boolean;
  /** Cap (px/s, magnitude) for the additive wall-jump's accumulated horizontal speed, so it
   *  can't diverge. 0 (the default) ⇒ fall back to `runMaxSpeed * 2`. Ignored when
   *  `wallJumpAdditive` is false. */
  wallJumpMaxSpeedX: number;

  // ─── Air-dash (archetype — DASH-PRECISION; generalizes COMBAT's dash) ──────────
  // COMBAT's dash is horizontal-only + COOLDOWN-gated + kills along the path. DASH-PRECISION
  // reuses the SAME primitive (dashVelocity/dashSteps + the committed-lunge branch) with the
  // gating switched to a CHARGE model via this one field, and a 2-D aim (the Player reads the
  // 8-way intent at the dash press). INERT (0) for COMBAT ⇒ it stays on the cooldown model,
  // byte-identical.
  /**
   * Max air-dash CHARGES (DASH-PRECISION). 0 = the COOLDOWN model (COMBAT — gate on
   * dashCooldownSteps, a connect re-arms). > 0 = the CHARGE model: a dash CONSUMES one charge,
   * charges refill to this max on LANDING (and a dashRefill pickup restores them mid-air).
   * Celeste's "no refresh until you land" is the whole tension. v1 ships 1.
   */
  dashMaxCharges: number;

  // ─── Stamina wall-climb (archetype — DASH-PRECISION) ──────────────────────────
  // The one genuinely-new DASH-PRECISION primitive: a stamina scalar that drains while
  // gripping a wall (the `grab` input) and refills on the ground. INERT (0) elsewhere — a
  // form without the climb verb never reads these, so it stays byte-identical. Integer units
  // (drain-per-step is integer) → deterministic across a replay.
  /** Max climb stamina (abstract units). 0 = no climb. Drains by `climbDrainPerStep`/step
   *  while gripping; at 0 the grip fails and the player falls into the wall-slide. */
  climbStaminaMax: number;
  /** Stamina drained each fixed step while actively climbing a wall (integer → deterministic). */
  climbDrainPerStep: number;
  /** Vertical climb speed (px/s, magnitude) while gripping a wall with stamina — applied up
   *  (negative vy). 0 = no climb. */
  climbSpeed: number;
  /**
   * DASH-FREEZE / hitstop (DASH-PRECISION / Celeste, feel-research 2026-06-30). Celeste
   * freezes the game for a few frames at the START of a dash — the committal "punch" that
   * makes the dash read as a discrete, weighty action (community-measured ≈ 3 frames; the
   * exact value is NOT in the reachable shipped source, so this is feel-tunable). When > 0,
   * an 8-way CHARGE dash (DASH-PRECISION only — COMBAT's cooldown dash never sets it) holds
   * the player frozen in place for this many fixed steps BEFORE the lunge integrates. Integer
   * step counter → replay-deterministic. 0 (the default) = no freeze → COMBAT's dash + every
   * other archetype bit-identical.
   */
  dashFreezeSteps: number;

  // ─── Player offense verbs (archetype — COMBAT) ────────────────────────────────
  // The data behind the `attack` (directional melee + pogo) and `dash` (dash-kill)
  // verbs. INERT at the base defaults (0) so a non-COMBAT form never arms a hitbox and
  // stays byte-identical: Player gates every read on attackEnabled / dashEnabled, both
  // off unless an archetype enables the verb. (Counts are in fixed sim STEPS, integer →
  // deterministic across a replay; px/s velocities follow the rest of the config.)
  /** Upward impulse (px/s, magnitude) of the automatic pogo bounce on a down-attack
   *  connect — the headline COMBAT feel knob. Applied as negative vy (no jump press). */
  pogoBounceVelocity: number;
  /** Fixed steps the attack hitbox stays live after a press (the swing window). */
  attackHitboxActiveSteps: number;
  /** Px the attack/dash hitbox extends past the body in the aim direction. */
  attackHitboxReach: number;
  /** Fixed steps before `attack` can fire again (anti-spam). */
  attackCooldownSteps: number;
  /** Small recoil (px/s) applied OPPOSITE the strike direction on a connect — feel +
   *  hazard. Down-strike → up (covered by the bounce, but applied so side strikes recoil). */
  pogoKnockbackVelocity: number;
  /** Horizontal lunge speed (px/s, magnitude) of a dash-kill while it is active. */
  dashVelocity: number;
  /** Fixed steps the dash-kill lunge lasts (a committed flat air-dash). */
  dashSteps: number;
  /** Fixed steps before `dash` can fire again (a connect refreshes it to 0 → chains). */
  dashCooldownSteps: number;

  // ─── Auto-run + dive (archetype — AUTO-RUNNER) ────────────────────────────────
  // Horizontal movement becomes AUTOMATIC: the player cruises at autoRunSpeed in the
  // current run direction (Player.autoRunDir, flipped by a wall-jump) and axisX is
  // deterministically IGNORED. INERT (0) elsewhere — autoRunSpeed 0 = the legacy
  // axisX-driven model, so every other archetype is byte-identical (Player gates the
  // whole branch on autoRunSpeed > 0). The DIVE is the air-only fast-fall verb (reuses
  // the `duck` input — NO new PackedInput bit). Real per-second values like the rest of
  // the config; integer / IEEE-754-exact ops only → replay-deterministic.
  /** Constant cruise speed (px/s) under AUTO-RUNNER. 0 = OFF (the legacy axisX model →
   *  CLASSIC / every other archetype bit-identical). > 0 turns the auto-run mode ON. */
  autoRunSpeed: number;
  /** DIVE (AUTO-RUNNER): max downward speed (px/s) of the hold-duck-in-air fast-fall.
   *  0 = no dive. Set > terminalVelocity so the dive overrides the normal terminal clamp. */
  diveSpeed: number;
  /** DIVE downward accel (px/s²) while diving — how fast vy ramps to diveSpeed. A SEPARATE
   *  knob from gravity / jumpReleaseGravity so the dive's punch tunes independently of jump
   *  feel. 0 = no dive accel (inert). */
  diveGravity: number;

  // ─── Player-fired projectile (archetype — RUN-AND-GUN) ─────────────────────────
  // The data behind the `shoot` verb — the one genuinely-new sim primitive (a player
  // bullet). INERT (0) elsewhere: projectileSpeed 0 = no gun, and Player gates the fire
  // path on the `shoot` verb (off for every other archetype), so a non-RUN-AND-GUN form
  // never spawns a bullet and stays byte-identical. All integer / IEEE-754-exact ops →
  // replay-deterministic. RUN-AND-GUN sets them > 0.
  /** Bullet travel speed (px/s). 0 = OFF (no gun → the fire path never spawns anything). */
  projectileSpeed: number;
  /** Fixed steps a bullet lives before it despawns on timeout (a missed shot never leaks).
   *  Integer step counter → deterministic. 0 = inert. */
  projectileLifeSteps: number;
  /** Fixed steps between shots — the fire-rate gate (caps the held auto-fire spray rate).
   *  Integer → deterministic. 0 = inert (no rate cap). */
  shootCooldownSteps: number;
}

/** Base/default config = the SMB1-ish "small" feel. Forms override partials of this. */
export const BASE_CONFIG: PhysicsConfig = {
  // Horizontal
  walkMaxSpeed: 200,
  runMaxSpeed: 360,
  groundAccel: 1300,
  groundDecel: 1900,
  airAccel: 750,
  airDecel: 520,
  skidDecel: 2700,
  skidThreshold: 35,
  airBrakeDecel: 0, // disabled → air-reversal stays on today's accel path (CLASSIC bit-identical)
  airDragMultiplier: 0, // 0 = flat air-coast (CLASSIC bit-identical); MOMENTUM sets ≈0.99
  // Vertical
  jumpVelocity: 720,
  gravity: 2050,
  jumpReleaseGravity: 4200,
  terminalVelocity: 1250,
  coyoteTimeMs: 90,
  jumpBufferMs: 120,
  apexHangThreshold: 0, // 0 = no apex-hang (CLASSIC bit-identical); DASH-PRECISION sets > 0
  apexHangGravity: 0,
  // Speed-dependent
  speedToJumpScale: 0.0,
  // Air authority — 0 = legacy independent air model (CLASSIC bit-identical).
  airControl: 0.0,
  // Wall verbs — inert defaults; a verb runs only when its archetype enables it.
  wallSlideMaxFallSpeed: 0,
  wallJumpPushX: 0,
  wallJumpVelocityY: 0,
  wallJumpControlLockSteps: 0,
  wallJumpAdditive: false, // SET vx (not additive) → PRECISION/every other archetype unchanged
  wallJumpMaxSpeedX: 0,
  // Air-dash charge model + stamina climb (DASH-PRECISION) — inert (0) elsewhere.
  dashMaxCharges: 0, // 0 = COMBAT's cooldown model (byte-identical); DASH-PRECISION sets > 0
  climbStaminaMax: 0,
  climbDrainPerStep: 0,
  climbSpeed: 0,
  dashFreezeSteps: 0, // 0 = no dash hitstop (COMBAT/every other archetype bit-identical); DASH-PRECISION sets > 0
  // Offense verbs (COMBAT) — inert defaults; a verb runs only when its archetype
  // enables it (attackEnabled / dashEnabled), so CLASSIC never arms a hitbox.
  pogoBounceVelocity: 0,
  attackHitboxActiveSteps: 0,
  attackHitboxReach: 0,
  attackCooldownSteps: 0,
  pogoKnockbackVelocity: 0,
  dashVelocity: 0,
  dashSteps: 0,
  dashCooldownSteps: 0,
  // Auto-run + dive (AUTO-RUNNER) — inert (0) elsewhere; autoRunSpeed 0 = legacy axisX model.
  autoRunSpeed: 0,
  diveSpeed: 0,
  diveGravity: 0,
  // Player-fired projectile (RUN-AND-GUN) — inert (0) elsewhere; projectileSpeed 0 = no gun,
  // and the fire path is gated on the `shoot` verb, so CLASSIC / every other archetype never
  // spawns a bullet and stays byte-identical.
  projectileSpeed: 0,
  projectileLifeSteps: 0,
  shootCooldownSteps: 0,
};

/**
 * Merge a partial override over a base config → a new fully-populated config.
 * This is the ONE mechanism behind both form presets AND the future
 * "user adjusts controls" UI. Forms are not special-cased; they write the same
 * partial that the tuning panel will write. Pure (does not mutate either input).
 */
export function mergeConfig(base: PhysicsConfig, override: Partial<PhysicsConfig>): PhysicsConfig {
  return { ...base, ...override };
}
